import { useActivePartnerEntity } from '@app/hooks/useActivePartnerEntity';
import { useAppDispatch, useAppSelector } from '@app/hooks/useReduxToolkit';
import {
	Avatar,
	Box,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	Tooltip,
	Typography,
} from '@mui/material';
import { signOut as SignOut } from 'aws-amplify/auth';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SessionTimer } from 'rhyme-component-library';
import {
	fetchPartnerUsers,
	fetchProviderDefaultContact,
	fetchProviderFacilities,
	resetDefaultContact,
	resetPartnerUsers,
	restFacilities,
	selectUserFullName,
	UserState,
} from 'state-services';
import { OrganizationMenu } from './OrganizationMenu';
import LogoutIcon from '@mui/icons-material/Logout';
import { EntityGroup, UserRoleTypes } from 'type-declarations';
import { EntityTypeActions } from 'user-utilities';

export const UserMenu: FC = () => {
	const userState = useAppSelector<UserState>((state) => state.user);
	const activePartnerEntity = useActivePartnerEntity();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = (): void => {
		setAnchorEl(null);
	};
	const dispatch = useAppDispatch();

	const userFullName = selectUserFullName(userState);
	const { name: partnerEntityName } = activePartnerEntity;
	const navigate = useNavigate();

	const signOut = (): void => {
		SessionTimer.stopSessionTimer();
		navigate('/');
		SignOut();
	};

	/**
	 * only call admin API's if user is
	 * providerAdmin or payerAdmin
	 */
	useEffect(() => {
		if (
			userState.activeScope?.roles.includes(UserRoleTypes.ProviderAdmin) ||
			userState.activeScope?.roles.includes(UserRoleTypes.PayerAdmin) ||
			userState.activeScope?.roles.includes(UserRoleTypes.DbmAdmin)
		) {
			EntityTypeActions.isInEntityGroup(
				activePartnerEntity.partnerType,
				EntityGroup.NOT_ORGANIZATION
			)
				? dispatch(fetchPartnerUsers())
				: (dispatch(fetchPartnerUsers()),
					dispatch(fetchProviderDefaultContact()),
					dispatch(fetchProviderFacilities()));
		} else {
			EntityTypeActions.isInEntityGroup(
				activePartnerEntity.partnerType,
				EntityGroup.NOT_ORGANIZATION
			)
				? dispatch(resetPartnerUsers())
				: (dispatch(resetDefaultContact()),
					dispatch(restFacilities()),
					dispatch(resetPartnerUsers()));
		}
	}, [activePartnerEntity]);

	return (
		<>
			<Box display="flex" alignItems="center">
				<Typography variant="body1" sx={{ mr: 1, color: 'black' }}>
					{partnerEntityName} / <b>{userFullName}</b>
				</Typography>
				<Tooltip title="User Settings">
					<IconButton
						onClick={handleClick}
						size="small"
						sx={{ ml: 2 }}
						aria-controls={open ? 'account-menu' : undefined}
						aria-haspopup="true"
						aria-expanded={open ? 'true' : undefined}
					>
						<Avatar />
					</IconButton>
				</Tooltip>
			</Box>
			<Menu
				anchorEl={anchorEl}
				id="account-menu"
				open={open}
				onClose={handleClose}
				onClick={handleClose}
				slotProps={{
					paper: {
						elevation: 0,
						sx: {
							overflow: 'visible',
							filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
							mt: 1.5,
							'& .MuiAvatar-root': {
								width: 32,
								height: 32,
								ml: -0.5,
								mr: 1,
							},
							'&::before': {
								content: '""',
								display: 'block',
								position: 'absolute',
								top: 0,
								right: 14,
								width: 10,
								height: 10,
								bgcolor: 'background.paper',
								transform: 'translateY(-50%) rotate(45deg)',
								zIndex: 0,
							},
						},
					},
				}}
				transformOrigin={{ horizontal: 'right', vertical: 'top' }}
				anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
			>
				<List dense>
					<OrganizationMenu />
					<ListItem>
						<ListItemButton onClick={signOut} sx={{ justifyContent: 'flex-end' }}>
							<ListItemIcon>
								<LogoutIcon />
							</ListItemIcon>
							<ListItemText>Sign Out</ListItemText>
						</ListItemButton>
					</ListItem>
				</List>
			</Menu>
		</>
	);
};
